/**
 * UpgradeProgressModal Component
 *
 * Displays real-time upgrade progress with step tracking and status.
 *
 * @module features/tenants/components/modals/UpgradeProgressModal
 */

import React from 'react';
import { CheckCircleIcon, AlertIcon, RefreshIcon, UndoIcon } from '../../../../components/common/Icons';
import type { Tenant } from '../../types';
import type { UpgradeStatus } from '../../api/upgradeApi';

interface UpgradeProgressModalProps {
  isOpen: boolean;
  tenant: Tenant | null;
  status: UpgradeStatus | null;
  onClose: () => void;
  onRollback: () => void;
  rolling: boolean;
}

const UPGRADE_STEPS = [
  'validating',
  'backing_up',
  'stopping',
  'migrating',
  'updating_compose',
  'starting',
  'validating_post',
];

const STEP_LABELS: Record<string, string> = {
  validating: 'Validation',
  backing_up: 'Creating backup',
  stopping: 'Stopping containers',
  migrating: 'Running migrations',
  updating_compose: 'Updating configuration',
  starting: 'Starting new containers',
  validating_post: 'Health check',
};

export const UpgradeProgressModal: React.FC<UpgradeProgressModalProps> = ({
  isOpen,
  tenant,
  status,
  onClose,
  onRollback,
  rolling,
}) => {
  if (!isOpen || !tenant || !status) return null;

  const isTerminal = ['completed', 'failed', 'rolled_back'].includes(status.status);
  const isFailed = status.status === 'failed';
  const isCompleted = status.status === 'completed';
  const isRolledBack = status.status === 'rolled_back';
  const currentStepIndex = UPGRADE_STEPS.indexOf(status.current_step || '');

  const elapsed = status.started_at
    ? Math.floor((Date.now() - new Date(status.started_at).getTime()) / 1000)
    : 0;
  const elapsedStr = elapsed > 0 ? `${Math.floor(elapsed / 60)}m ${elapsed % 60}s` : '';

  return (
    <div className="modal-overlay" onClick={isTerminal ? onClose : undefined}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
        <div className="modal-header">
          <h2>
            {isCompleted ? 'Upgrade Complete' : isFailed ? 'Upgrade Failed' : isRolledBack ? 'Upgrade Rolled Back' : `Upgrading: ${tenant.name}`}
          </h2>
          {isTerminal && <button className="btn btn-ghost" onClick={onClose}>×</button>}
        </div>
        <div className="modal-body">
          {/* Version bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '13px' }}>
            <span>{status.from_version}</span>
            <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${status.progress_pct}%`,
                background: isFailed ? 'var(--error)' : isCompleted ? 'var(--success)' : 'var(--primary)',
                transition: 'width 0.5s ease',
              }} />
            </div>
            <span>{status.to_version}</span>
          </div>

          {/* Progress percentage */}
          <div style={{ textAlign: 'center', marginBottom: '20px', fontSize: '14px', fontWeight: 600 }}>
            {status.progress_pct}%
          </div>

          {/* Steps */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
            {UPGRADE_STEPS.map((step, i) => {
              const isDone = i < currentStepIndex || isCompleted;
              const isCurrent = step === status.current_step && !isTerminal;
              const isErrorStep = step === status.error_step;

              return (
                <div key={step} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                  {isDone && <CheckCircleIcon size={16} style={{ color: 'var(--success)' }} />}
                  {isCurrent && <RefreshIcon size={16} className="spin" style={{ color: 'var(--primary)' }} />}
                  {isErrorStep && <AlertIcon size={16} style={{ color: 'var(--error)' }} />}
                  {!isDone && !isCurrent && !isErrorStep && (
                    <span style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)', display: 'inline-block' }} />
                  )}
                  <span style={{
                    opacity: isDone || isCurrent || isErrorStep ? 1 : 0.4,
                    color: isErrorStep ? 'var(--error)' : undefined,
                  }}>
                    {STEP_LABELS[step] || step}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Error message */}
          {isFailed && status.error_message && (
            <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)', fontSize: '13px', color: 'var(--error)', marginBottom: '16px' }}>
              {status.error_message}
            </div>
          )}

          {/* Elapsed time */}
          {elapsedStr && (
            <div style={{ fontSize: '12px', opacity: 0.5, textAlign: 'center' }}>
              Elapsed: {elapsedStr}
            </div>
          )}
        </div>
        <div className="modal-footer">
          {isFailed && status.backup_id && (
            <button className="btn btn-secondary" onClick={onRollback} disabled={rolling}>
              <UndoIcon size={14} /> {rolling ? 'Rolling back...' : 'Rollback'}
            </button>
          )}
          {isTerminal && (
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          )}
        </div>
      </div>
    </div>
  );
};
